import React, { useEffect, useRef, useState } from "react";
import {
  Play, Pause, Download, Loader2, RotateCcw, RotateCw,
  Volume2, VolumeX, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { downloadOrShareFile } from "../../utils/pdfShare";

const SPEEDS = [1, 1.25, 1.5, 2, 0.75];

export function formatClock(secs) {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * CallRecordingPlayer — the ONE shared audio player for call recordings and
 * voicemails across Fieldz AI (Call History, Job Recordings, Voicemails).
 *
 * Play/pause · draggable seek bar · current/total time · ±10s skip ·
 * playback speed · mute (full variant) · download · buffering + error/retry.
 *
 * Props:
 *   src            — proxied (authed) audio URL. Backend supports Range.
 *   knownDuration  — seconds from the DB (Twilio RecordingDuration); used
 *                    whenever the browser can't discover duration itself.
 *   fileName       — download filename, e.g. Call_Recording_Job_123_2026-08-12.mp3
 *   autoPlay       — start playing on mount (row players)
 *   compact        — smaller controls for inline rows
 *   active         — when false, this player pauses (one-at-a-time control)
 *   onPlayStart    — notify parent so it can pause sibling players
 *   onEnded        — playback finished naturally
 *   showDownload   — hide when the host row already has a download button
 */
const CallRecordingPlayer = ({
  src,
  knownDuration = 0,
  fileName = "Call_Recording.mp3",
  autoPlay = false,
  compact = false,
  active = true,
  onPlayStart,
  onEnded,
  showDownload = true,
  testId = "recording-player",
}) => {
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const resumeAtRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragRatio, setDragRatio] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const duration = Number.isFinite(mediaDuration) && mediaDuration > 0 ? mediaDuration : (knownDuration || 0);
  const displayTime = dragRatio != null && duration > 0 ? dragRatio * duration : currentTime;
  const progress = duration > 0 ? Math.min(100, (displayTime / duration) * 100) : 0;

  // One-at-a-time: pause when another player takes over.
  useEffect(() => {
    if (!active && audioRef.current && !audioRef.current.paused) audioRef.current.pause();
  }, [active]);

  // Stop audio when unmounted (modal closed / row switched).
  useEffect(() => () => { try { audioRef.current?.pause(); } catch { /* gone */ } }, []);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      onPlayStart?.();
      a.playbackRate = SPEEDS[speedIdx];
      a.play().catch(() => setError(true));
    } else {
      a.pause();
    }
  };

  const seekTo = (ratio) => {
    const a = audioRef.current;
    if (!a || duration <= 0) return;
    const t = Math.max(0, Math.min(Math.max(duration - 0.25, 0), ratio * duration));
    try { a.currentTime = t; setCurrentTime(t); } catch { /* not seekable yet */ }
  };

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    const max = duration > 0 ? Math.max(duration - 0.25, 0) : a.currentTime + Math.max(delta, 0);
    const t = Math.max(0, Math.min(max, a.currentTime + delta));
    try { a.currentTime = t; setCurrentTime(t); } catch { /* not seekable yet */ }
  };

  const ratioFromEvent = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
  };

  const onBarPointerDown = (e) => {
    if (duration <= 0) return;
    e.preventDefault();
    try { barRef.current.setPointerCapture?.(e.pointerId); } catch { /* older browsers */ }
    setDragRatio(ratioFromEvent(e));
  };
  const onBarPointerMove = (e) => { if (dragRatio != null) setDragRatio(ratioFromEvent(e)); };
  const onBarPointerUp = (e) => {
    if (dragRatio == null) return;
    seekTo(ratioFromEvent(e));
    setDragRatio(null);
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  const handleRetry = () => {
    resumeAtRef.current = currentTime;
    setError(false);
    setErrorDetail(null);
    setBuffering(false);
    setReloadKey((k) => k + 1);
  };

  const handleDownload = async (e) => {
    e?.stopPropagation();
    if (!src) return;
    setDownloading(true);
    const token = localStorage.getItem("token");
    try {
      await downloadOrShareFile(src, fileName, "audio/mpeg", token);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Download failed");
        const sep = src.includes("?") ? "&" : "?";
        window.open(`${src}${sep}download=1&filename=${encodeURIComponent(fileName)}`, "_blank");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div
        className={`flex items-center gap-2 rounded-lg border border-red-500/25 bg-red-500/[0.06] px-3 w-full ${compact ? "py-1.5" : "py-2.5"}`}
        data-testid={`${testId}-error`}
      >
        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
        <span className="text-xs text-red-400 flex-1 min-w-0 truncate" title={errorDetail || undefined}>
          {errorDetail || "Unable to load recording. Try again."}
        </span>
        <button
          type="button"
          onClick={handleRetry}
          className="text-xs font-medium text-red-400 hover:text-red-300 flex items-center gap-1 shrink-0"
          data-testid={`${testId}-retry-btn`}
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const mainBtn = compact ? "h-8 w-8" : "h-10 w-10";
  const smallBtn = compact ? "h-6 w-6" : "h-7 w-7";
  const smallIcon = compact ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <div className={`flex items-center ${compact ? "gap-1.5" : "gap-2"} w-full min-w-0`} data-testid={testId}>
      <audio
        key={reloadKey}
        ref={audioRef}
        src={src}
        preload="metadata"
        autoPlay={autoPlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
        onTimeUpdate={(e) => { if (dragRatio == null) setCurrentTime(e.target.currentTime || 0); }}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.target.duration) && e.target.duration > 0) setMediaDuration(e.target.duration);
          if (resumeAtRef.current > 0) {
            try { e.target.currentTime = resumeAtRef.current; } catch { /* not seekable */ }
            resumeAtRef.current = 0;
          }
        }}
        onDurationChange={(e) => {
          if (Number.isFinite(e.target.duration) && e.target.duration > 0) setMediaDuration(e.target.duration);
        }}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onError={(e) => {
          const mediaErr = e.target?.error;
          if (src && mediaErr && mediaErr.code !== mediaErr.MEDIA_ERR_ABORTED) {
            setError(true);
            // Surface the backend's specific reason (404 detail etc.) —
            // the <audio> element can't expose the response body itself.
            fetch(src, { headers: { Range: "bytes=0-1" } })
              .then(async (r) => {
                if (r.ok) return;
                try {
                  const d = await r.json();
                  if (typeof d?.detail === "string") setErrorDetail(d.detail);
                } catch { /* non-JSON body */ }
              })
              .catch(() => {});
          }
        }}
      />

      {/* Play / Pause */}
      <button
        type="button"
        onClick={togglePlay}
        className={`${mainBtn} rounded-full shrink-0 flex items-center justify-center transition-colors ${
          playing ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/85" : "bg-[#FFCC00]/15 text-[#B8930A] dark:text-[#FFCC00] hover:bg-[#FFCC00]/25"
        }`}
        data-testid={`${testId}-play-btn`}
        aria-label={playing ? "Pause" : "Play"}
      >
        {buffering && playing ? (
          <Loader2 className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} animate-spin`} />
        ) : playing ? (
          <Pause className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
        ) : (
          <Play className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} ml-0.5`} />
        )}
      </button>

      {/* Back 10s / Forward 10s */}
      <button
        type="button"
        onClick={() => skip(-10)}
        className={`${smallBtn} rounded-full shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors`}
        data-testid={`${testId}-back10-btn`}
        aria-label="Back 10 seconds"
      >
        <RotateCcw className={smallIcon} />
      </button>
      <button
        type="button"
        onClick={() => skip(10)}
        className={`${smallBtn} rounded-full shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors`}
        data-testid={`${testId}-fwd10-btn`}
        aria-label="Forward 10 seconds"
      >
        <RotateCw className={smallIcon} />
      </button>

      {/* Progress bar + time readout */}
      <div className="flex-1 min-w-0">
        <div
          ref={barRef}
          onPointerDown={onBarPointerDown}
          onPointerMove={onBarPointerMove}
          onPointerUp={onBarPointerUp}
          onPointerCancel={() => setDragRatio(null)}
          className={`relative ${compact ? "h-1.5" : "h-2"} bg-muted rounded-full ${duration > 0 ? "cursor-pointer" : "cursor-default"} touch-none group`}
          data-testid={`${testId}-seek-bar`}
        >
          <div
            className="absolute inset-y-0 left-0 bg-[#FFCC00] rounded-full"
            style={{ width: `${progress}%` }}
          />
          {duration > 0 && (
            <div
              className={`absolute top-1/2 -translate-y-1/2 ${compact ? "w-2.5 h-2.5" : "w-3.5 h-3.5"} rounded-full bg-white border-2 border-[#FFCC00] shadow ${dragRatio != null ? "opacity-100 scale-110" : "opacity-0 group-hover:opacity-100"} transition-opacity`}
              style={{ left: `calc(${progress}% - ${compact ? 5 : 7}px)` }}
            />
          )}
        </div>
        <div
          className={`flex justify-between font-mono text-muted-foreground ${compact ? "text-[9px]" : "text-[10px]"} mt-0.5`}
          data-testid={`${testId}-time`}
        >
          <span data-testid={`${testId}-current-time`}>{formatClock(displayTime)}</span>
          <span data-testid={`${testId}-total-duration`}>{duration > 0 ? formatClock(duration) : "--:--"}</span>
        </div>
      </div>

      {/* Playback speed */}
      <button
        type="button"
        onClick={cycleSpeed}
        className={`shrink-0 rounded-md px-1.5 ${compact ? "h-6 text-[9px]" : "h-7 text-[10px]"} font-mono font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors min-w-[34px]`}
        data-testid={`${testId}-speed-btn`}
        aria-label="Playback speed"
      >
        {SPEEDS[speedIdx]}x
      </button>

      {/* Mute (full variant only) */}
      {!compact && (
        <button
          type="button"
          onClick={toggleMute}
          className={`${smallBtn} rounded-full shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors`}
          data-testid={`${testId}-mute-btn`}
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className={smallIcon} /> : <Volume2 className={smallIcon} />}
        </button>
      )}

      {/* Download */}
      {showDownload && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className={`${smallBtn} rounded-full shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50`}
          data-testid={`${testId}-download-btn`}
          aria-label="Download recording"
        >
          {downloading ? <Loader2 className={`${smallIcon} animate-spin`} /> : <Download className={smallIcon} />}
        </button>
      )}
    </div>
  );
};

export default CallRecordingPlayer;
